import React, { useState, useEffect } from 'react'
import "./Home.css"
import { NavLink } from 'react-router-dom'
import { HomeIcon, ResultsIcon, LeaderboardIcon, ProfileIcon } from '../Icons'
import BlogCard from '../Blog/BlogCard'
import { blogPosts } from '../../data/blogPosts'
import Navbar from '../Shared/Navbar'
import ChatBox from '../Chat/ChatBox'

const Home = () => {
  const [isChatOpen, setIsChatOpen] = useState(false)

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen)
  }

  return (
    <div className="home-page">
      <Navbar />

      {/* Main Content */}
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Welcome to <span className="highlight">Practcode AI</span></h1>
              <p>
                Learn programming with ease using our AI-powered platform. 
                Explore courses, participate in contests, and enhance your skills.
              </p>
              <div className="hero-buttons">
                <NavLink to="/user/signup" className="primary-btn">
                  Get Started
                </NavLink>
                <NavLink to="/courses" className="secondary-btn">
                  Explore Courses
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Aptitude & Reasoning Section */}
        <section className="aptitude-section">
          <div className="aptitude-content">
            <h2>Enhance Your Logical Thinking</h2>
            <p>
              Prepare for competitive exams and placements with our comprehensive Aptitude and Reasoning tests. 
              Improve your problem-solving skills and logical reasoning abilities with tailored practice sessions.
            </p>
            <div className="aptitude-features">
              <div className="aptitude-feature">
                <i className="fa-solid fa-puzzle-piece"></i>
                <span>Logical Reasoning</span>
              </div>
              <div className="aptitude-feature">
                <i className="fa-solid fa-calculator"></i>
                <span>Quantitative Aptitude</span>
              </div>
              <div className="aptitude-feature">
                <i className="fa-solid fa-clock"></i>
                <span>Time Management</span>
              </div>
              <div className="aptitude-feature">
                <i className="fa-solid fa-chart-bar"></i>
                <span>Performance Tracking</span>
              </div>
            </div>
            <NavLink to="/Aptitude-test" className="primary-btn">
              Start Aptitude Test
            </NavLink>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2>Why Choose Practcode Ai?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <HomeIcon />
              </div>
              <h3>AI-Powered Learning</h3>
              <p>Personalized learning paths adapted to your skill level and goals</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <LeaderboardIcon />
              </div>
              <h3>Competitive Programming</h3>
              <p>Regular coding competitions to test and improve your skills</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <ResultsIcon />
              </div>
              <h3>Comprehensive Courses</h3>
              <p>Structured courses covering all major programming languages</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <ProfileIcon />
              </div>
              <h3>Community Support</h3>
              <p>Join a community of learners and get help when you need it</p>
            </div>
          </div>
        </section>

        {/* Seven Days Coding Challenge Section */}
        <section className="seven-days-challenge-section">
          <div className="seven-days-challenge-content">
            <h2>Seven Days Coding Challenge</h2>
            <p>
              Take your coding skills to the next level with our structured 7-day challenge. Each day unlocks a new milestone, guiding you from the basics to advanced problem-solving. Stay consistent, track your progress, and get ready for real competitive programming!
            </p>
            <div className="seven-days-challenge-features">
              <div className="seven-days-challenge-feature">
                <i className="fa-solid fa-bolt"></i>
                <span>Daily Milestones</span>
              </div>
              <div className="seven-days-challenge-feature">
                <i className="fa-solid fa-trophy"></i>
                <span>Progress Tracking</span>
              </div>
              <div className="seven-days-challenge-feature">
                <i className="fa-solid fa-users"></i>
                <span>Community Support</span>
              </div>
              <div className="seven-days-challenge-feature">
                <i className="fa-solid fa-medal"></i>
                <span>Certificate on Completion</span>
              </div>
            </div>
            <NavLink to="/Seven-days-challange" className="primary-btn" style={{ marginTop: "1.5rem" }}>
              View Challenge
            </NavLink>
          </div>
        </section>

        {/* Pre Placement Analysis Section */}
        <section className="pre-placement-section">
          <div className="pre-placement-content">
            <div className="pre-placement-text">
              <h2>Pre Placement Analysis</h2>
              <p>Get a comprehensive analysis of your skills and readiness for placement interviews. Our AI-powered system evaluates your performance across multiple domains to provide personalized insights and improvement recommendations.</p>
              <div className="pre-placement-features">
                <div className="pre-placement-feature">
                  <i className="fa-solid fa-chart-line"></i>
                  <span>Performance Analytics</span>
                </div>
                <div className="pre-placement-feature">
                  <i className="fa-solid fa-brain"></i>
                  <span>Skill Assessment</span>
                </div>
                <div className="pre-placement-feature">
                  <i className="fa-solid fa-lightbulb"></i>
                  <span>Improvement Tips</span>
                </div>
                <div className="pre-placement-feature">
                  <i className="fa-solid fa-briefcase"></i>
                  <span>Placement Readiness</span>
                </div>
              </div>
              <NavLink to="/Student-analysis/Result/User_result" className="primary-btn">
                Try Pre Placement Analysis
              </NavLink>
            </div>
          </div>
        </section>

        {/* Courses Section */}
        <section className="courses-section">
          <div className="section-header">
            <h2>Popular Courses</h2>
            <NavLink to="/courses" className="view-all">
              View All Courses
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="arrow-icon">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </NavLink>
          </div>
          <div className="courses-grid">
            <div className="course-card">
              <div className="course-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>Java Programming</h3>
              <p>Master Java from basics to advanced concepts</p>
              <div className="course-meta">
                <span>8 Weeks</span>
                <span>Beginner</span>
              </div>
              <a href="/courses/java" className="course-btn">Start Learning</a>
            </div>

            <div className="course-card">
              <div className="course-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3z"/>
                  <path d="M12 8v8M8 12h8"/>
                </svg>
              </div>
              <h3>C Programming</h3>
              <p>Learn the fundamentals of C programming</p>
              <div className="course-meta">
                <span>6 Weeks</span>
                <span>Beginner</span>
              </div>
              <a href="/courses/c" className="course-btn">Start Learning</a>
            </div>

            <div className="course-card">
              <div className="course-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  <path d="M12 12l-5 2.5L12 17l5-2.5L12 12z"/>
                </svg>
              </div>
              <h3>C++ Programming</h3>
              <p>Advanced C++ concepts and OOP principles</p>
              <div className="course-meta">
                <span>8 Weeks</span>
                <span>Intermediate</span>
              </div>
              <a href="/courses/cpp" className="course-btn">Start Learning</a>
            </div>

            <div className="course-card">
              <div className="course-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  <path d="M12 12l-5 2.5L12 17l5-2.5L12 12z"/>
                  <path d="M12 12l-5 2.5L12 17l5-2.5L12 12z"/>
                </svg>
              </div>
              <h3>Python Programming</h3>
              <p>Python for beginners and data science</p>
              <div className="course-meta">
                <span>6 Weeks</span>
                <span>Beginner</span>
              </div>
              <a href="/courses/python" className="course-btn">Start Learning</a>
            </div>

            <div className="course-card">
              <div className="course-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  <path d="M12 12l-5 2.5L12 17l5-2.5L12 12z"/>
                  <path d="M12 12l-5 2.5L12 17l5-2.5L12 12z"/>
                  <path d="M12 12l-5 2.5L12 17l5-2.5L12 12z"/>
                </svg>
              </div>
              <h3>Aptitude & Reasoning</h3>
              <p>Master logical reasoning and aptitude</p>
              <div className="course-meta">
                <span>4 Weeks</span>
                <span>All Levels</span>
              </div>
              <a href="/courses/aptitude" className="course-btn">Start Learning</a>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section className="blog-section">
          <div className="section-header">
            <h2>Latest from Our Blog</h2>
            <NavLink to="/blog" className="view-all">
              View All Posts
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="arrow-icon">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </NavLink>
          </div>
          <div className="blog-grid">
            {blogPosts.map(blog => (
              <BlogCard key={blog.id} blog={blog} />
            ))}
          </div>
        </section>

        {/* Competition Banner */}
        <section className="competition-banner">
          <div className="banner-content">
            <h2>Regular Coding Competitions</h2>
            <p>Our platform regularly hosts coding competitions to help you improve your skills and compete with peers</p>
            <div className="banner-buttons">
              <NavLink to="/Codingcompetition/2025/leaderboard" className="primary-btn">
                View Leaderboard
              </NavLink>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Practcode Ai</h3>
            <p>Empowering developers through AI-driven learning</p>
          </div>
          <div className="footer-section">
            <h3>Quick Links</h3>
            <NavLink to="/user/login">Login</NavLink>
            <NavLink to="/user/signup">Sign Up</NavLink>
            <NavLink to="/Codingcompetition/2025/result">Results</NavLink>
            <NavLink to="/Codingcompetition/2025/leaderboard">Leaderboard</NavLink>
          </div>
          <div className="footer-section">
            <h3>Connect With Us</h3>
            <div className="social-links">
              <a href="#" className="social-link">Twitter</a>
              <a href="#" className="social-link">LinkedIn</a>
              <a href="#" className="social-link">GitHub</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 Practcode Ai. All rights reserved.</p>
        </div>
      </footer>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <NavLink to="/" className="mobile-nav-item">
          <HomeIcon />
          <span>Home</span>
        </NavLink>
        <NavLink to="/Codingcompetition/2025/result" className="mobile-nav-item">
          <ResultsIcon />
          <span>Results</span>
        </NavLink>
        <NavLink to="/Codingcompetition/2025/leaderboard" className="mobile-nav-item">
          <LeaderboardIcon />
          <span>Leaderboard</span>
        </NavLink>
        <NavLink to="/user/profile" className="mobile-nav-item">
          <ProfileIcon />
          <span>Profile</span>
        </NavLink>
      </div>

      {/* AI Chat Support Button */}
      <button className="ai-chat-button" onClick={toggleChat}>
        <div className="ai-chat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <span className="ai-chat-label">Chat with AI</span>
      </button>

      {/* ChatBox Component */}
      <ChatBox isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  )
}

export default Home